'use client'
import { CarouselSize } from '@/app/(home)/_components/carousel'
import CarouselReviews from '@/app/(home)/_components/carousel_reviews'
import Loading from '@/components/loading'
import { Button } from '@/components/ui/button'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'
import { GroupService } from '@/services/group.service'
import { IGroup } from '@/types/group.types'
import Cookies from 'js-cookie'
import { Gauge, GlobeLock, Headset, Rocket } from 'lucide-react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Suspense, useEffect, useState } from 'react'
import { brand } from '@/config/brand'
import FAQSection from '@/app/(home)/_components/faq-active.generated'
import FishBackground from '@/app/(home)/_components/fish-background'
import SpotlightCards from '@/app/(home)/_components/spotlight-cards'
import SplashCursor from '@/app/(home)/_components/splash-cursor'

// Separate component for handling search params
function ReferralHandler({
	onReferralCode,
}: {
	onReferralCode: (code: string) => void
}) {
	const searchParams = useSearchParams()

	useEffect(() => {
		const referralCode = searchParams.get('referral_code')
		if (referralCode) {
			onReferralCode(referralCode)
		}
	}, [searchParams, onReferralCode])

	return null
}

export default function Home() {
	const [isLoading, setIsLoading] = useState(true)
	const [auth, setAuth] = useState('false')
	const [groups, setGroups] = useState<IGroup | null>(null)
	const router = useRouter()

	useEffect(() => {
		setAuth(Cookies.get('isAuth') || 'false')
		// const accessToken = Cookies.get('accessToken')
		// const refreshToken = Cookies.get('refreshToken')
		// if (!accessToken && refreshToken) {
		// 	AuthService.getNewTokens()
		// 		.then(e => {
		//
		// 		})
		// 		.catch(error => {
		// 			toast.error(error.response.data.message || 'Ошибка входа')
		// 		})
		// }

		GroupService.getGroups()
			.then(response => {
				if (response) {
					setGroups(response)
					setIsLoading(false)
				}
			})
			.catch(error => {
				console.log(error)
			})
	}, [])

	const handleReferralCode = (code: string) => {
		localStorage.setItem('referralCode', code)
	}

	return (
		<>
			<Suspense>
				<ReferralHandler onReferralCode={handleReferralCode} />
			</Suspense>
			<Suspense fallback={<Loading />}>
				<div className='relative min-h-screen bg-gradient-to-b from-black to-gray-900'>
					<FishBackground />
					<SplashCursor />
					<div className='relative z-10 container mx-auto px-4 py-20'>
						{/* Hero Section */}
						<div className='text-center space-y-6 mb-20'>
							<h1 className='skuf-obstacle w-fit mx-auto text-4xl md:text-6xl lg:text-[80px] font-bold leading-tight'>
								<span className='text-[#bfff01] block mb-2'>{brand.copy.heroTitleAccent}</span>
								<span className='text-white'>{brand.copy.heroTitleRest}</span>
							</h1>

							<p className='skuf-obstacle text-gray-300 text-lg md:text-xl max-w-2xl mx-auto mb-4'>
								{brand.copy.heroSubtitle}
							</p>
							{auth === 'true' ? (
								<Button
									onClick={() => {
										window.location.href = '/profile'
									}}
									className='bg-[#bfff01] text-black hover:bg-[#9fdf00] text-lg px-8 py-6 rounded-full transition-all duration-300 font-semibold'
								>
									Личный кабинет
								</Button>
							) : (
								<button
									className='bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-full hover:opacity-90 transition-opacity duration-300 text-sm lg:text-base'
									onClick={() => router.push('/auth/fastregister')}
								>
									Начать бесплатно
								</button>
							)}
						</div>

						{/* Features Section */}
						<p className='skuf-obstacle w-fit text-white text-2xl uppercase mb-4' id='features'>
							{'[Преимущества]'}
						</p>
						<div className='mb-20'>
							<h2 className='skuf-obstacle w-fit mx-auto text-3xl md:text-4xl font-bold text-white text-center mb-4'>
								{brand.copy.featuresTitle}
							</h2>
							<p className='skuf-obstacle text-gray-300 text-center max-w-3xl mx-auto mb-12 text-lg'>
								{brand.copy.featuresSubtitle}
							</p>

							<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
								<Card className='bg-black/50 backdrop-blur-sm border border-gray-800 p-6 rounded-xl hover:transform hover:scale-105 transition-all duration-300'>
									<div className='flex flex-col items-center text-center space-y-4'>
										<div className='p-4 rounded-full bg-blue-500/10'>
											<Rocket className='w-20 h-20 text-blue-500' />
										</div>
										<p className='text-white font-medium'>
											Высокая скорость и стабильность соединения до 10 Гбит/сек
										</p>
									</div>
								</Card>

								<Card className='bg-black/50 backdrop-blur-sm border border-gray-800 p-6 rounded-xl hover:transform hover:scale-105 transition-all duration-300'>
									<div className='flex flex-col items-center text-center space-y-4'>
										<div className='p-4 rounded-full bg-[#bfff01]/10'>
											<Gauge className='w-20 h-20 text-[#bfff01]' />
										</div>
										<p className='text-white font-medium'>
											Моментальное подключение
										</p>
									</div>
								</Card>

								<Card className='bg-black/50 backdrop-blur-sm border border-gray-800 p-6 rounded-xl hover:transform hover:scale-105 transition-all duration-300'>
									<div className='flex flex-col items-center text-center space-y-4'>
										<div className='p-4 rounded-full bg-[#bfff01]/10'>
											<Headset className='w-20 h-20 text-[#bfff01]' />
										</div>
										<p className='text-white font-medium'>
											Личный менеджер 24/7
										</p>
									</div>
								</Card>

								<Card className='bg-black/50 backdrop-blur-sm border border-gray-800 p-6 rounded-xl hover:transform hover:scale-105 transition-all duration-300'>
									<div className='flex flex-col items-center text-center space-y-4'>
										<div className='p-4 rounded-full bg-blue-500/10'>
											<GlobeLock className='w-20 h-20 text-blue-500' />
										</div>
										<p className='text-white font-medium'>
											{brand.copy.featureCardSecurity}
										</p>
									</div>
								</Card>
							</div>
						</div>
						<div className='text-center flex flex-col sm:flex-row justify-between items-center gap-4 mb-10'>
							<p
								className='skuf-obstacle text-white text-xl sm:text-2xl uppercase'
								id='tariffs'
							>
								{'[Тарифный план]'}
							</p>
							<p className='skuf-obstacle text-white text-xl sm:text-2xl'>
								Выберите тариф, который подходит именно вам
							</p>
						</div>
						<SpotlightCards spotlightRadius={300} glowColor='132, 0, 255'>
						<div>
							{groups == null ? (
								<Loading />
							) : (
								<div className='flex justify-center items-center'>
									<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 lg:gap-8 max-w-[1400px] items-stretch justify-center px-4 sm:px-6'>
										{groups.items.flatMap(group =>
											group.products.map(product => (
												<Card
													key={product.id}
													className='skuf-obstacle spotlight-card bg-black border border-gray-800 hover:transform hover:scale-105 transition-all duration-300 flex flex-col h-[360px] sm:h-[400px] w-full sm:max-w-[280px] md:max-w-[300px] lg:max-w-[280px]'
												>
													<CardHeader className='space-y-2 sm:space-y-3 p-3 sm:p-4 md:p-6 h-[125px] sm:h-[135px] md:h-[145px] flex flex-col'>
														<CardTitle className='text-2xl sm:text-lg md:text-xl lg:text-2xl text-white font-bold text-center leading-tight'>
															{product.name}
														</CardTitle>
														{product.benefits &&
															product.benefits.length > 0 && (
																<CardDescription className='text-base sm:text-sm md:text-base text-gray-300 text-center leading-tight line-clamp-1 mt-auto'>
																	{product.benefits[0]}
																</CardDescription>
															)}
													</CardHeader>
													<CardContent className='p-3 sm:p-4 md:p-6 flex-1 flex flex-col justify-between'>
														<div className='flex-1 flex items-center justify-center'>
															<div className='flex flex-col items-center justify-center bg-black/20 py-3 sm:py-4 px-2 rounded-lg w-full'>
																{product.new_price ? (
																	<div className='flex flex-col items-center'>
																		<div className='flex items-center mb-1'>
																			<span className='text-gray-400 line-through text-lg md:text-lg'>
																				{product.price} ₽
																			</span>
																			<span className='ml-2 px-2 py-0.5 text-sm sm:text-sm bg-red-500/30 text-red-200 rounded-full'>
																				-
																				{Math.round(
																					(1 -
																						product.new_price / product.price) *
																						100
																				)}
																				%
																			</span>
																		</div>
																		<div className='text-[#bfff01] font-semibold text-3xl md:text-3xl lg:text-4xl'>
																			{product.new_price} ₽
																		</div>
																	</div>
																) : (
																	<div className='text-[#bfff01] font-semibold text-5xl md:text-5xl lg:text-5xl'>
																		{product.price} ₽
																	</div>
																)}
															</div>
														</div>

														<div className='mt-4'>
															{auth == 'true' ? (
																<Button
																	onClick={() => {
																		router.push(
																			`/profile/subscriptions/subscribe?product_id=${product.id}`
																		)
																	}}
																	className='w-full bg-[#bfff01] text-black hover:bg-[#9fdf00] transition-all duration-300 font-semibold text-lg md:text-lg py-2 md:py-3'
																>
																	Выбрать
																</Button>
															) : (
																<Button
																	onClick={() => {
																		router.push(
																			`/auth/fastregister?product_id=${product.id}`
																		)
																	}}
																	className='w-full bg-[#bfff01] text-black hover:bg-[#9fdf00] transition-all duration-300 font-semibold text-lg md:text-lg py-2 md:py-3'
																>
																	Выбрать
																</Button>
															)}
														</div>
													</CardContent>
												</Card>
											))
										)}
									</div>
								</div>
							)}
						</div>
						</SpotlightCards>
						<CarouselSize />
						<FAQSection />
						<div className='text-center  flex justify-between mb-4'>
							<p className='text-white text-2xl uppercase' id='reviews'>
								{'[Отзывы]'}
							</p>
							<p className='text-white text-2xl'>
								Вот, что о нас говорят наши клиенты
							</p>
						</div>
						<CarouselReviews />
					</div>
				</div>
			</Suspense>
		</>
	)
}
